'use client';

import type { ApiErrorType, ContactProps } from '@/core/types';
import type { ContactSchemaType } from '@/containers/blocks/forms/schema/contact-schema';

import React from 'react';
import { toast } from 'sonner';
import { CONFIG } from '@/global-config';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { useTranslate } from '@/core/locales';
import { processApiError } from '@/core/utils';
import { TRANSLATION_NAMESPACES } from '@/core/constants';

import { sendEmail } from '@/actions/contact';

import { Form, Field } from '@/components/hook-form';
import { Box, Image, Button, Container, Typography } from '@/components/ui';

import { contactSchema } from '@/containers/blocks/forms/schema/contact-schema';

export default function Contact({ title, subTitle, className = '' }: ContactProps) {
  const { t } = useTranslate(TRANSLATION_NAMESPACES.COMMON);
  const { t: tFooter } = useTranslate(TRANSLATION_NAMESPACES.FOOTER);

  const ContactSchema = contactSchema(t);

  const defaultValues: ContactSchemaType = {
    email: '',
    firstName: '',
    lastname: '',
    phone: '',
    message: '',
  };

  const methods = useForm<ContactSchemaType>({
    resolver: zodResolver(ContactSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    setError,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = handleSubmit(async (data) => {
    try {
      await sendEmail(data);
      reset();
      toast.success(t('forms.contact.success'));
    } catch (error) {
      processApiError(error as ApiErrorType, setError, t, t('forms.contact.error'));
    }
  });

  return (
    <div className={`relative w-full bg-light-gray/56 ${className} `}>
      <Container className="flex flex-col items-center justify-center text-center">
        <Box className="w-full max-w-[970px]">
          {title && (
            <Typography
              variant="h2"
              className="text-2xl text-center md:text-4xl lg:leading-[61px] font-bold mb-6 uppercase"
            >
              {title}
            </Typography>
          )}
          {subTitle && (
            <Typography
              variant="body1"
              paragraph
              className="hidden text-center md:block text-lg  md:text-xl mb-8 mb-[48px]"
            >
              {subTitle}
            </Typography>
          )}
        </Box>
        <Form
          methods={methods}
          onSubmit={onSubmit}
          className="w-full grid lg:grid-cols-[30%_70%] bg-white rounded-2xl"
        >
          <Box className="relative z-1">
            <Image
              src={`${CONFIG.assetsDir}/assets/images/contact/contact-bg.webp`}
              alt="контакт"
              width={1440}
              height={800}
              objectFit="cover"
              className="w-full absolute top-0 left-0 h-full object-cover -z-[1] lg:rounded-l-2xl rounded-t-2xl lg:rounded-t-none"
              loading="eager"
            />

            <Box className="text-white text-left p-8 lg:px-[64px] lg:py-[66px]">
              <Typography
                variant="h3"
                component="h1"
                className="uppercase lg:text-[42px] mb-4 lg:mb-[40px]"
              >
                {t('contact.title')}
              </Typography>
              <Box className="flex flex-col gap-3 lg:gap-[40px]">
                <Typography variant="h6" className="font-light">
                  {tFooter('footer.contacts.entrepreneur')}
                </Typography>
                <Typography variant="h6" className="font-light">
                  {tFooter('footer.contacts.taxId')}
                </Typography>
                <Typography variant="h6" className="font-light">
                  {tFooter('footer.contacts.businessId')}
                </Typography>
                <a
                  href={`mailto:${tFooter('footer.contacts.emailLink')}`}
                  className="font-light hover:underline text-lg"
                >
                  {tFooter('footer.contacts.email')}
                </a>
              </Box>
            </Box>
          </Box>
          <Box className="p-8 grid gap-4 lg:gap-6">
            <Box className="flex font-black text-2xl text-primary-dark uppercase">
              {t('contact.formTitle')}
            </Box>
            <Box className="grid grid-cols-2 gap-4 lg:gap-6">
              <Field.Text className="lg:text-lg" name="firstName" label={t('fields.label.name')} />
              <Field.Text
                className="lg:text-lg"
                name="lastname"
                label={t('fields.label.lastname')}
              />
              <Field.Text className="lg:text-lg" name="phone" label={t('fields.label.phone')} />
              <Field.Text className="lg:text-lg" name="email" label={t('fields.label.email')} />
            </Box>
            <Box>
              <Field.Textarea
                className="lg:text-lg"
                name="message"
                label={t('fields.label.message')}
              />
            </Box>
            <Box className="flex">
              <Button
                type="submit"
                variant="contained"
                loading={isSubmitting}
                loadingText={t('actions.sending')}
              >
                {t('actions.send')}
              </Button>
            </Box>
          </Box>
        </Form>
      </Container>
    </div>
  );
}
